import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { CurrentUserPermissions, getCurrentUserPermissions } from 'api/users';
import useTenantSelection from 'hooks/useTenantSelection';
import useAuth from 'hooks/useAuth';
import { permissionContextKey } from 'utils/permissionContext';

// Super-user permission (see Permissions.ManageApplication on the API) — grants all permissions.
const MANAGE_APPLICATION_PERMISSION = 'manage:application';

const EMPTY_CODES: string[] = [];

interface UsePermissionsResult {
  permissionCodes: string[];
  roleName: string | null;
  // Every role held at the level the roles resolved from — roleName is the most privileged of them.
  roleNames: string[];
  roleLevel: number | null;
  loading: boolean;
  // Set once the lookup has failed and SWR has stopped retrying. Lets callers that would otherwise
  // wait indefinitely on `loading` fall back to rendering with no permissions.
  error: unknown;
  hasPermission: (permissionCode: string) => boolean;
}

/**
 * The current user's role/permissions for the selected tenant/company/site, resolved server-side by
 * PermissionEnrichmentMiddleware.
 *
 * Backed by SWR rather than per-component state: a dozen components call this hook, and one request
 * per context is shared between them. That also means a failed fetch is retried by SWR instead of
 * leaving whichever component owned it stuck with no permissions — which showed up as a silently
 * truncated menu, since every nav section is permission-gated.
 */
export default function usePermissions(): UsePermissionsResult {
  const { tenantId, selectedCompanySiteResult, contextLoading } = useTenantSelection();
  const { user } = useAuth();
  const companyId = selectedCompanySiteResult?.companyId ?? null;
  const siteId = selectedCompanySiteResult?.siteId ?? null;
  const contextKey = permissionContextKey(tenantId, companyId, siteId);

  const { data, error } = useSWR<CurrentUserPermissions>(
    contextLoading || contextKey == null ? null : `${contextKey}::user:${user?.id ?? '_'}`,
    () => getCurrentUserPermissions({ tenantId: tenantId!, companyId, siteId }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // No keepPreviousData: while a new tenant's permissions are in flight the hook reports
      // loading with no codes, so permission-gated UI fails closed rather than briefly rendering
      // the previous tenant's access.
      keepPreviousData: false
    }
  );

  const permissionCodes = data?.permissionCodes ?? EMPTY_CODES;

  const hasPermission = useCallback(
    (permissionCode: string) => permissionCodes.includes(MANAGE_APPLICATION_PERMISSION) || permissionCodes.includes(permissionCode),
    [permissionCodes]
  );

  // "Loading" means "no authoritative answer for the selected tenant yet" rather than SWR's
  // isLoading, which goes false in the gaps — between the key changing and the fetch starting,
  // while the tenant-change sweep has the entry cleared, and between failed retries. Consumers
  // treat not-loading + no permission as a denial, so a false gap there redirects to access-denied
  // or silently drops menu sections. Erring towards loading only delays that decision.
  const loading = contextLoading || (tenantId != null && data === undefined && !error);

  return useMemo(
    () => ({
      permissionCodes,
      roleName: data?.roleName ?? null,
      roleNames: data?.roleNames ?? EMPTY_CODES,
      roleLevel: data?.roleLevel ?? null,
      loading,
      error,
      hasPermission
    }),
    [permissionCodes, data, loading, error, hasPermission]
  );
}
